import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ChevronUp, ChevronDown, Sparkles, Copy, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { API_BASE_URL } from '../config/api';

// Field purpose descriptions for context
const FIELD_PURPOSES = {
  'Project Name': 'The name of this customer journey mapping project',
  'Problem Statement': 'A clear description of the customer problem this product solves',
  'Cost Amount': 'The financial cost of this problem to customers (e.g., $50K/year)',
  'Cost Justification': 'Explanation of how/why this problem costs money',
  'TAM Amount': 'Total Addressable Market size in dollars',
  'TAM Justification': 'How you calculated or estimated the market size',
  'Current Solutions Fail': 'Why existing solutions don\'t adequately solve this problem',
  'Market Opportunity': 'The business opportunity this problem represents',
  'Expected Moat': 'Your competitive advantage or barrier to entry',
  'Persona Name': 'A descriptive name for your target customer persona',
  'Role / Segment': 'The job role or market segment of your persona',
  'Product Statement': 'A one-sentence description of what your product does',
  'Feature Name': 'The name of a key product feature',
  'Feature Value': 'The benefit this feature provides to customers',
  'Feature Evidence': 'Proof points like metrics, testimonials, or case studies',
  'Solution Name': 'Name of a solution that addresses a specific problem',
  'Solution Description': 'How this solution solves the problem',
  'Hero': 'Who is the main character in your customer story (usually the customer)',
  'Quest': 'What outcome or goal is the customer trying to achieve',
  'Villains': 'The obstacles, pains, or friction blocking the customer',
  'Guide': 'What helps the customer (your product/features)',
  'Moment of Truth': 'When the customer realizes your solution is right for them',
  'Success': 'The tangible positive outcome after using your solution',
  'Action': 'What the customer is doing at this journey stage',
  'Thoughts': 'What questions or considerations the customer has',
  'Feeling': 'The emotional state of the customer at this stage'
};

const JourneyAIAssistant = ({ activeFieldId, currentStep, journeyData }) => {
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeFieldName, setActiveFieldName] = useState('No field selected');
  const [currentFieldValue, setCurrentFieldValue] = useState('');

  // Track active field name and current value
  useEffect(() => {
    if (activeFieldId) {
      const element = document.getElementById(activeFieldId);
      if (element) {
        if (element.dataset.fieldName) {
          setActiveFieldName(element.dataset.fieldName);
        }
        setCurrentFieldValue(element.value || '');
      }
    }
  }, [activeFieldId]);

  // Get field purpose based on field name
  const getFieldPurpose = (fieldName) => {
    // Try exact match first
    if (FIELD_PURPOSES[fieldName]) return FIELD_PURPOSES[fieldName];
    
    // Try partial matches
    for (const [key, value] of Object.entries(FIELD_PURPOSES)) {
      if (fieldName.includes(key) || key.includes(fieldName.split(' ').pop())) {
        return value;
      }
    }
    return '';
  };

  const generateResponse = async () => {
    if (!userPrompt.trim()) {
      toast({ title: 'Empty prompt', description: 'Please enter a question or request', variant: 'destructive' });
      return;
    }

    setIsGenerating(true);
    setAiResponse('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/journey/ai-assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_prompt: userPrompt,
          field_name: activeFieldName,
          field_purpose: getFieldPurpose(activeFieldName),
          current_field_value: currentFieldValue,
          journey_context: journeyData
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setAiResponse(data.content);
      } else {
        throw new Error(data.error || 'AI request failed');
      }
      
    } catch (error) {
      console.error('AI generation error:', error);
      setAiResponse('Sorry, I encountered an error. Please try again.');
      toast({ title: 'Generation failed', description: error.message || 'Please try again', variant: 'destructive' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseThis = () => {
    if (!activeFieldId || !aiResponse) {
      toast({ title: 'Cannot fill', description: 'No field selected or no response', variant: 'destructive' });
      return;
    }

    const element = document.getElementById(activeFieldId);
    if (element) {
      // Append to existing content or replace if empty
      const existingValue = element.value || '';
      const newValue = existingValue 
        ? `${existingValue}\n\n${aiResponse}` 
        : aiResponse;
      
      // Trigger React onChange
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        'value'
      )?.set || Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;
      
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(element, newValue);
        const event = new Event('input', { bubbles: true });
        element.dispatchEvent(event);
      }

      toast({ title: 'Applied!', description: existingValue ? 'Content added to field' : 'AI response filled into field' });
      setAiResponse('');
      setUserPrompt('');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(aiResponse);
    toast({ title: 'Copied!', description: 'Response copied to clipboard' });
  };

  if (!isExpanded) {
    return (
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg shadow-md cursor-pointer hover:from-orange-600 hover:to-amber-600 transition-colors mb-4 sm:mb-6"
           onClick={() => setIsExpanded(true)}>
        <div className="px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="text-xs sm:text-sm md:text-base font-semibold truncate">AI Assistant - Tap for help with any field</span>
          </div>
          <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-orange-500 rounded-lg shadow-xl mb-4 sm:mb-6 max-h-[500px]">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between cursor-pointer flex-shrink-0 rounded-t-lg"
             onClick={() => setIsExpanded(false)}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="text-sm sm:text-base font-semibold">AI Assistant</span>
          </div>
          <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
          {/* Active Field Indicator */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm">
            <span className="font-semibold text-blue-900">Active:</span>
            <span className="text-blue-700 ml-1 sm:ml-2">{activeFieldName}</span>
            {!activeFieldId && (
              <span className="text-blue-600 ml-1 sm:ml-2 italic block sm:inline">(Tap any field above)</span>
            )}
          </div>

          {/* Input */}
          <div>
            <label className="text-xs sm:text-sm font-medium text-slate-700 block mb-2">Ask for help:</label>
            <Textarea
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              placeholder="e.g., 'Help me write a problem statement'"
              rows={2}
              className="resize-none text-sm"
            />
            <div className="flex gap-2 mt-2">
              <Button 
                onClick={generateResponse} 
                disabled={isGenerating || !userPrompt.trim()}
                className="flex-1"
                size="sm"
              >
                {isGenerating ? 'Generating...' : 'Generate'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => { setUserPrompt(''); setAiResponse(''); }}
                size="sm"
              >
                Clear
              </Button>
            </div>
          </div>

          {/* Response */}
          {aiResponse && (
            <div>
              <label className="text-xs sm:text-sm font-medium text-slate-700 block mb-2">AI Response:</label>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 sm:p-4 max-h-32 overflow-y-auto">
                <p className="text-xs sm:text-sm text-slate-800 whitespace-pre-wrap">{aiResponse}</p>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button 
                  onClick={handleUseThis}
                  disabled={!activeFieldId}
                  className="flex-1 min-w-[120px]"
                  size="sm"
                >
                  <Check className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Use This</span>
                </Button>
                <Button variant="outline" onClick={handleCopy} size="sm">
                  <Copy className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Copy</span>
                </Button>
                <Button variant="outline" onClick={() => setAiResponse('')} size="sm">
                  <span className="text-xs sm:text-sm">Discard</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JourneyAIAssistant;
